import type { FC } from 'react';
import React, { useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FAStateContext } from '../context/FAStateContext';
import { NavigationButton } from './NavigationButton';
import { Image } from './slider/Image';
import { useTrackingManager } from '../hooks/useTracking';
import { ContentContext } from '../context/ContentContext';
import { UniversalStateContext } from '../context/UniversalStateContext';
import { updateAltWithI18n } from '../utils/imgI18nAlt';

export const ASPECT_RATIO_SMALL = '4 /3';
export const ASPECT_RATIO_MOBILE_LANDSCAPE = '3 / 1';
export const ASPECT_RATIO_MEDIUM = '4 / 3';
export const ASPECT_RATIO_LARGE = '19 / 9';

export const AspectRatioContainer = styled.div`
  position: relative;
  aspect-ratio: ${ASPECT_RATIO_SMALL};
  width: 100%;
  overflow: hidden;
  @media (min-width: ${(props) => props.theme.breakpoints.md - 1}px) and (orientation: landscape) {
    aspect-ratio: ${ASPECT_RATIO_MOBILE_LANDSCAPE};
  }

  @media (min-width: ${(props) => props.theme.breakpoints.md}px) {
    aspect-ratio: ${ASPECT_RATIO_MEDIUM};
  }
  @media (min-width: ${(props) => props.theme.breakpoints.lg}px) {
    aspect-ratio: ${ASPECT_RATIO_LARGE};
  }
`;

export const ScreenReaderOnly = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

const Slides = styled.div<{ currentSlide: number }>`
  display: flex;
  transition: transform 0.5s ease;
  transform: ${({ currentSlide }) => `translateX(-${currentSlide * 100}%)`};
`;

const NavigationContainer = styled.div`
  display: flex;
  position: absolute;
  bottom: 50%;
  left: 0;
  right: 0;
  justify-content: space-between;
  transform: translateY(50%);
  margin: ${process.env.IS_LEGACY ? '0' : '0 var(--page-margin)'};
`;

export const Slider: FC = () => {
  const { images, carline } = useContext(FAStateContext);
  const { name: carlineName } = carline;
  const [imageIndex, setImageIndex] = useContext(FAStateContext).imageIndexState;
  const slideCount = images.length;
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const trackingManager = useTrackingManager();
  const { carlineId } = useContext(ContentContext);
  const { i18nTexts } = useContext(UniversalStateContext)!;

  const ariaLabelSlide = i18nTexts['ariaLabel.imageSlide'].replace(
    // eslint-disable-next-line no-template-curly-in-string
    '${value}',
    carlineName ?? '',
  );
  const nextSlide = () => {
    setImageIndex((prev) => {
      const newIndex = (prev + 1) % slideCount;
      trackImageChange(prev, newIndex);
      return newIndex;
    });
  };

  const prevSlide = () => {
    setImageIndex((prev) => {
      const newIndex = (prev - 1 + slideCount) % slideCount;
      trackImageChange(prev, newIndex);
      return newIndex;
    });
  };

  const trackImageChange = (prevIndex: number, newIndex: number) => {
    trackingManager.click({
      eventAction: 'image',
      eventName: 'change image',
      elementName: 'arrow',
      value: carlineId,
      pos: `${prevIndex + 1}~${newIndex + 1}`,
    });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      nextSlide();
    }

    if (touchStartX.current - touchEndX.current < -50) {
      prevSlide();
    }
  };

  useEffect(() => {
    trackingManager.ready(__FEATURE_APP_VERSION__);
  }, []);
  return (
    <AspectRatioContainer role="group" aria-roledescription="carousel" aria-label={ariaLabelSlide}>
      <Slides
        currentSlide={imageIndex}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {images.map(({ url, altText }, index) => (
          <Image key={url} url={url} altText={altText} index={index} />
        ))}
      </Slides>
      <NavigationContainer>
        <NavigationButton onClick={prevSlide} disabled={imageIndex === 0} direction="back" />
        <NavigationButton
          onClick={nextSlide}
          disabled={imageIndex + 1 === slideCount}
          direction="forward"
        />
      </NavigationContainer>
      <ScreenReaderOnly aria-live="polite">
        {`Slide ${imageIndex + 1} of ${images.length}:  ${updateAltWithI18n(images[imageIndex]?.altText) || 'Image'}`}
      </ScreenReaderOnly>
    </AspectRatioContainer>
  );
};
